import * as KeyMap from '@packages/utilities/key-map';

/**
 * KeySet: Represents an efficient set-like data structure optimized for AWS S3 storage.
 *
 * Under the hood, it's a `KeyMap<true>` (a `{[key: string]: true}`).
 *
 * Benefits:
 * - Minimizes serialization/deserialization costs in S3
 * - Type-safe for string keys
 */
export type KeySet = KeyMap.Type<true>;
export type Type = KeySet;

/**
 * Creates a KeySet out of a single key.
 */
export const singleton = (key: string): KeySet => KeyMap.singleton(key, true);

/**
 * Adds a new key to the KeySet.
 */
export const add = (set: KeySet, key: string): KeySet =>
  KeyMap.assoc(key)(true as const)(set);

const addKeyToSet = (acc: KeySet, key: string): KeySet => {
  acc[key] = true;
  return acc;
};

/**
 * Creates a new KeySet from an array of keys.
 */
export const fromArray = (initialKeys: Array<string>): KeySet => {
  if (initialKeys.length === 0) return KeyMap.empty<true>();
  return initialKeys.reduce(addKeyToSet, {} as KeySet);
};

/**
 * Converts the KeySet to an array of keys.
 */
export const toArray: (set: KeySet) => Array<string> = KeyMap.keys;

/**
 * Removes a key from the KeySet.
 */
export const remove = (set: KeySet, key: string): KeySet => {
  const newSet = KeyMap.dissoc(key)(set);
  // Can’t add this optimization to `dissoc` because some existing consumers
  // mutate the map resulting from `dissoc`.
  return size(newSet) === 0 ? empty() : newSet;
};

/**
 * Checks if a key exists in the KeySet.
 */
export const has = (set: KeySet, key: string) => KeyMap.has(key)(set);

/**
 * Iterates over each key in the KeySet.
 */
export const forEach = (set: KeySet, callback: (key: string) => void): void => {
  toArray(set).forEach(callback);
};

/**
 * Creates an empty KeySet.
 */
export const empty: () => KeySet = KeyMap.empty;

/**
 * Gets the number of keys in the KeySet.
 */
export const size = (set: KeySet): number => toArray(set).length;
